import { ChevronDownIcon, HomeIcon } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/components/Base/Collapsible";
import { useState } from "react";
import type { RoleType } from "@repo/types";
import { RoleProtected } from "../common/RoleProtected";
import { cn } from "@repo/shared/utils";

export type LinkItem = {
  label: string;
  icon?: React.ReactNode;
  href: string;
  minRole?: RoleType;
};

export type CollapsibleSection = {
  title: string;
  icon?: React.ReactNode;
  linkItems: LinkItem[];
  minRole?: RoleType;
};

interface SidebarProps {
  linkItems: (LinkItem | CollapsibleSection)[][];
}

const LinkItem = ({ link, subLink }: { link: LinkItem; subLink?: boolean }) => {
  return (
    <li className="w-full my-1 flex flex-row">
      <Link to={link.href} className="flex flew-grow w-full">
        <button
          className={cn(
            "px-2 py-2 w-full text-left text-secondary-foreground flex flex-row items-center border-b",
            subLink && "ml-4"
          )}
        >
          {link.icon}
          {link.label}
        </button>
      </Link>
    </li>
  );
};

const CollapsibleItem = (section: CollapsibleSection) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(!isOpen && "border-b")}
    >
      <CollapsibleTrigger className="px-2 py-2 w-full text-left rounded-md text-secondary-foreground flex flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          {section.icon}
          {section.title}
        </div>
        <ChevronDownIcon
          className={cn("transition duration-300", isOpen && "rotate-180")}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {section.linkItems.map((item, iIndex) => {
          if (item.minRole) {
            return (
              <RoleProtected
                key={`${section.title}-${iIndex}`}
                minRole={item.minRole}
              >
                <LinkItem link={item} subLink />
              </RoleProtected>
            );
          } else {
            return <LinkItem link={item} subLink />;
          }
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const Drawer = ({ linkItems }: SidebarProps) => {
  return (
    <aside className="p-2 w-52 bg-primary mt-20 mb-12 text-sm overflow-auto">
      <ul className="w-full">
        <li className="w-full border-b">
          <Link to={`/app`}>
            <button className="px-2 py-2 w-full rounded-md text-secondary-foreground flex flex-row items-center">
              <HomeIcon className="h-4 w-4 mr-2" />
              Home
            </button>
          </Link>
        </li>
        <li className="border-2 border-primary my-1"></li>
        <li suppressHydrationWarning>
          {linkItems?.map((section, sIndex) => (
            <ul key={sIndex}>
              {section.map((item, iIndex) => {
                if ("title" in item) {
                  if (item.minRole) {
                    return (
                      <RoleProtected key={iIndex} minRole={item.minRole}>
                        <CollapsibleItem {...item} />
                      </RoleProtected>
                    );
                  }
                  return <CollapsibleItem key={iIndex} {...item} />;
                } else {
                  if (item.minRole) {
                    return (
                      <RoleProtected key={iIndex} minRole={item.minRole}>
                        <LinkItem link={item} />
                      </RoleProtected>
                    );
                  } else {
                    return <LinkItem key={iIndex} link={item} />;
                  }
                }
              })}
            </ul>
          ))}
        </li>
      </ul>
    </aside>
  );
};
